import * as React from 'react';
import { FileInput, IFileInputProps } from '../../../file-input';
import { action, observable } from 'mobx';
import { findIndex } from 'lodash';
import { inject, observer } from 'mobx-react';
import { getUniqueId } from '../../../../util/string/get-unique-id.util';
import { IInjectedProps } from '../../../../interface/component/injected-props.interface';
import { chunkFile } from '../../../../util/blob/chunk-file.util';
import { AjaxError, AjaxResponse } from 'rxjs';

export interface IFileData {
    id: string;
    isComplete: boolean;
    fileName: string;
    contentType: string;
}

type IFileFieldProps = IFileInputProps & { files: string[], uploadFileErrorMessagePrefix: string, onChange: (files: IFileData[]) => void, onFileSizeError: () => void, onFileNameError: () => void, onError: (error: AjaxError) => void, onUploadStarted: (size: number) => void, onUploadFinished: () => void };

interface IFileContainer {
    id: string;
    serverResponse: IFileData;
    file: File;
}

@inject('service')
@observer
export class FileField extends React.Component<IFileFieldProps & IInjectedProps> {
    @observable files: IFileContainer[] = [];
    @observable ongoingUploads: { id: string, error: Error, fileSize: number, totalChunks: number, parts: { start: number, end: number, file: Blob }[] }[] = [];

    // fix for google drive documents changing datemodified on the fly :(
    isChromeOnAndroid = () => {
        return /Android/i.test(navigator.userAgent) && /Chrome/i.test(navigator.userAgent);
    }
    uploadError = (fileId: string) => action((response: AjaxError) => {
        const index = findIndex(this.ongoingUploads, { id: fileId });
        if (index !== -1) {
            const fileUploadProgress = this.ongoingUploads[index];
            fileUploadProgress.error = response;
            this.props.onError(response);
        }
    })

    uploadPartComplete = (fileId: string, start: number, end: number) => action((response: AjaxResponse) => {
        const serverResponse: IFileData = response.response as IFileData;
        const fileIndex = findIndex(this.files, { id: fileId });
        const file = this.files[fileIndex];
        const shouldUpdate = !file.serverResponse || file.serverResponse.id !== serverResponse.id;
        file.serverResponse = serverResponse;
        if (shouldUpdate) {
            this.props.onChange(this.files.map((fileContainer) => fileContainer.serverResponse));
        }
        const index = findIndex(this.ongoingUploads, { id: fileId });
        if (index !== -1) {
            const fileUploadProgress = this.ongoingUploads[index];
            const partIndex = findIndex(fileUploadProgress.parts, { start });
            const newParts = fileUploadProgress.parts.slice();
            newParts.splice(partIndex, 1);
            fileUploadProgress.parts = newParts;
            if (fileUploadProgress.parts.length > 0) {
                const part = fileUploadProgress.parts[0];
                this.sendFilePart(fileId, serverResponse, part.start, part.end, part.file);
            } else {
                this.stopFileUpload(fileId);
            }
        }
    })

    sendFilePart = (fileId: string, serverResponse: IFileData, start: number, end: number, filePart: Blob) => {
        const index = findIndex(this.ongoingUploads, { id: fileId });
        const fileIndex = findIndex(this.files, { id: fileId });
        let promise;

        if (!this.isChromeOnAndroid()) {
            const fieldId = serverResponse ? serverResponse.id : undefined;
            const fileName = (this.files[fileIndex].file as any).uploadName || this.files[fileIndex].file.name;
            const fileSize = this.ongoingUploads[index].fileSize;
            promise = this.props.service.uploadService.uploadChunk(fieldId, fileName, fileSize, start, end, filePart).toPromise();
        } else {
            const fileName = (this.files[fileIndex].file as any).uploadName || this.files[fileIndex].file.name;
            const fileSize = this.ongoingUploads[index].fileSize;
            // For android we upload file at once to a bug in the mobile browsers
            promise = this.props.service.uploadService.uploadChunk(undefined, fileName, fileSize, start, end, this.files[fileIndex].file).toPromise();
        }
        promise.then(this.uploadPartComplete(fileId, start, end))
            .catch(this.uploadError(fileId));
    }

    uploadFilePart = (fileId: string) => (start: number, end: number, file: Blob) => {
        const index = findIndex(this.ongoingUploads, { id: fileId });
        const fileIndex = findIndex(this.files, { id: fileId });
        const fileMetadata = this.files[fileIndex];
        if (index !== -1) {
            const fileUploadProgress = this.ongoingUploads[index];
            if (fileUploadProgress.parts.length === 0) {
                this.sendFilePart(fileId, fileMetadata.serverResponse, start, end, file);
            }
            fileUploadProgress.parts.push({ start, end, file });
        }
    }

    startFileUpload = (fileId: string) => (fileSize: number, totalChunks: number) => {
        const index = findIndex(this.ongoingUploads, { id: fileId });
        if (index !== -1) {
            this.ongoingUploads[index].fileSize = fileSize;
            this.ongoingUploads[index].totalChunks = totalChunks;
        }
    }

    @action
    stopFileUpload = (fileId: string) => {
        const index = findIndex(this.ongoingUploads, { id: fileId });
        if (index !== -1) {
            const newOA = this.ongoingUploads.slice();
            newOA.splice(index, 1);
            this.ongoingUploads = newOA;
            if (newOA.length === 0) {
                this.props.onUploadFinished();
            }
        }
    }

    @action
    uploadFile = (file: IFileContainer) => {
        if (this.ongoingUploads.length === 0) {
            this.props.onUploadStarted(file.file.size);
        }
        const newOA = this.ongoingUploads.slice();
        newOA.push({ id: file.id, fileSize: 0, error: undefined, totalChunks: 0, parts: [] });
        this.ongoingUploads = newOA;
        chunkFile(file.file, this.uploadFilePart(file.id), this.startFileUpload(file.id));
    }

    @action
    onChange = (files: File[]) => {

        files.forEach((file) => {
            const index = findIndex(this.files, (currentFile) =>
                currentFile.file.name === file.name &&
                currentFile.file.size === file.size &&
                (this.isChromeOnAndroid() ? true : currentFile.file.lastModified === file.lastModified)
            );
            if (index === -1) {
                const fileContainer = {
                    id: getUniqueId(),
                    serverResponse: undefined,
                    file: file
                };
                if (!this.props.multiple && this.files.length > 0) {
                    this.stopFileUpload(this.files[0].id);
                }
                const fileNameRegex = /[<>:"/\|?*]/g;
                if (fileNameRegex.test(fileContainer.file.name)) {
                    this.props.onFileNameError();
                    return;
                }
                if (fileContainer.file.size > this.props.fileSizeLimit) {
                    this.props.onFileSizeError();
                    return;
                }
                if (this.props.multiple) {
                    this.files.push(fileContainer);
                } else {
                    this.files = [fileContainer];
                }
                this.uploadFile(fileContainer);
            }
        });
        for (let i = this.files.length - 1; i >= 0; i--) {
            const fileContainer = this.files[i];
            const foundIndex = findIndex(files, (currentFile) =>
                fileContainer.file.name === currentFile.name &&
                fileContainer.file.size === currentFile.size &&
                (this.isChromeOnAndroid() ? true : fileContainer.file.lastModified === currentFile.lastModified)
            );
            if (foundIndex === -1) {
                this.stopFileUpload(fileContainer.id);
                this.files.splice(i, 1);
            }
        }
        this.props.onChange(this.files.map((fileContainer) => fileContainer.serverResponse));
    }

    @action
    onCancelFile = (file: File) => {
        const index = findIndex(this.files, (currentFile) =>
            currentFile.file.name === file.name &&
            currentFile.file.size === file.size &&
            (this.isChromeOnAndroid() ? true : currentFile.file.lastModified === file.lastModified)
        );
        if (index !== -1) {
            const fileId = this.files[index].id;
            const newFiles = this.files.slice();
            newFiles.splice(index, 1);
            this.files = newFiles;
            this.props.onChange(this.files.map((fileContainer) => fileContainer.serverResponse));
            this.stopFileUpload(fileId);
        }
    }

    @action
    onReloadFile = (file: File) => {
        const index = findIndex(this.files, (currentFile) =>
            currentFile.file.name === file.name &&
            currentFile.file.size === file.size &&
            (this.isChromeOnAndroid() ? true : currentFile.file.lastModified === file.lastModified)
        );
        if (index !== -1) {
            this.stopFileUpload(this.files[index].id);
            this.uploadFile(this.files[index]);
        }
    }

    render() {
        const { files, ...props } = this.props;
        return <FileInput
            {...props}
            fileProgress={this.files.map((fc) => {
                const index = findIndex(this.ongoingUploads, { id: fc.id });
                if (index === -1) {
                    return { complete: 1, error: false, total: 1 };
                } else {
                    return { complete: (this.ongoingUploads[index].totalChunks - this.ongoingUploads[index].parts.length), error: Boolean(this.ongoingUploads[index].error), total: this.ongoingUploads[index].totalChunks };
                }
            })}
            files={this.files.map((fileContainer) => fileContainer.file)}
            onChange={this.onChange}
            onCancel={this.onCancelFile}
            onReload={this.onReloadFile}
        />;
    }
}